import React from 'react';
import { Box, Flex, HStack, Text, IconButton, Tag, Wrap, WrapItem } from '@chakra-ui/react';
import { Handle, Position } from 'reactflow';
import { FiEdit, FiCopy, FiTrash2, FiGitMerge } from 'react-icons/fi';

const HEADER_PX = 44;
const BODY_PX = 120;
const NODE_TOTAL_PX = HEADER_PX + BODY_PX;

const getDotTop = (index, total) => {
    const usable = BODY_PX - 24;
    const step = total > 1 ? usable / (total - 1) : 0;
    const offsetPx = HEADER_PX + 12 + index * step;
    return `${(offsetPx / NODE_TOTAL_PX) * 100}%`;
};

const MergeNode = ({ data }) => {
    const inputCount = data.inputCount || 3;
    const mergeBehavior = data.mergeBehavior || 'any';
    const continueOnce = data.continueOnce !== false;
    const timeoutEnabled = data.timeoutEnabled || false;
    const timeoutValue = data.timeoutValue || 30;
    const timeoutUnit = data.timeoutUnit || 'minutes';

    return (
        <Box
            bg="white"
            borderWidth="2px"
            borderColor={data.selected ? '#7c3aed' : '#8b5cf6'}
            borderRadius="xl"
            boxShadow={data.selected ? 'xl' : 'lg'}
            width="280px"
            position="relative"
        >
            {/* Left — incoming branch handles */}
            {Array.from({ length: inputCount }, (_, i) => (
                <Handle
                    key={i}
                    type="target"
                    position={Position.Left}
                    id={`merge-in-${i}`}
                    style={{
                        background: '#8b5cf6',
                        width: '12px',
                        height: '12px',
                        border: '2px solid white',
                        top: getDotTop(i, inputCount),
                    }}
                />
            ))}

            {/* Header */}
            <Flex
                bgGradient="linear(to-r, #8b5cf6, var(--brand-primary))"
                color="white"
                px={4}
                py={2.5}
                borderTopRadius="xl"
                align="center"
                justify="space-between"
            >
                <HStack spacing={2}>
                    <FiGitMerge />
                    <Text noOfLines={1} fontSize="sm" fontWeight="medium">Merge</Text>
                </HStack>
                <HStack spacing={1}>
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiEdit />}
                        onClick={(e) => { e.stopPropagation(); data.onEdit?.(data.id); }}
                        aria-label="Edit"
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiCopy />}
                        onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}
                        aria-label="Duplicate"
                    />
                    <IconButton
                        size="xs"
                        variant="ghost"
                        color="white"
                        icon={<FiTrash2 />}
                        onClick={(e) => { e.stopPropagation(); data.onDelete?.(data.id); }}
                        aria-label="Delete"
                    />
                </HStack>
            </Flex>

            {/* Body */}
            <Box p={3.5} minH={`${BODY_PX}px`}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={3}>
                    {data.label || 'Merge Branches'}
                </Text>

                {/* Branch count pill */}
                <HStack mb={3}>
                    <Tag
                        colorScheme="purple"
                        variant="subtle"
                        borderRadius="full"
                        fontSize="11px"
                        px={2}
                        py={0.5}
                    >
                        ● {inputCount} incoming branch{inputCount !== 1 ? 'es' : ''}
                    </Tag>
                </HStack>

                {/* Behavior badges */}
                <Wrap spacing={1.5}>
                    <WrapItem>
                        <Tag
                            colorScheme={mergeBehavior === 'all' ? 'blue' : 'green'}
                            variant="subtle"
                            borderRadius="full"
                            fontSize="11px"
                            px={2}
                            py={0.5}
                        >
                            {mergeBehavior === 'all' ? 'Wait for all' : 'First to complete'}
                        </Tag>
                    </WrapItem>
                    {continueOnce && (
                        <WrapItem>
                            <Tag
                                colorScheme="orange"
                                variant="subtle"
                                borderRadius="full"
                                fontSize="11px"
                                px={2}
                                py={0.5}
                            >
                                Once only
                            </Tag>
                        </WrapItem>
                    )}
                    {timeoutEnabled && (
                        <WrapItem>
                            <Tag
                                colorScheme="gray"
                                variant="subtle"
                                borderRadius="full"
                                fontSize="11px"
                                px={2}
                                py={0.5}
                            >
                                ⏱ {timeoutValue}{timeoutUnit === 'hours' ? 'h' : 'm'}
                            </Tag>
                        </WrapItem>
                    )}
                </Wrap>
            </Box>

            {/* Right — single merged output */}
            <Handle
                type="source"
                position={Position.Right}
                id="merge-out"
                style={{
                    background: 'var(--brand-primary)',
                    width: '14px',
                    height: '14px',
                    border: '2px solid white',
                    top: '50%',
                }}
            />
        </Box>
    );
};

export default MergeNode;
